// ============================================
// FILE: src/components/fashion/FashionSpecsAccordion.jsx
// ============================================
//
// Flat stacked sections rather than collapsed accordions: on a fashion product
// page the details are the reason the shopper scrolled, so they are open by
// default. Only the long description collapses behind a "More" toggle.
//
// Every block is skipped when its data is missing, so a product with no specs
// degrades to just its description.

import React, { useState } from 'react';

const DETAIL_FIELDS = [
  ['fabric', 'Fabric'],
  ['fit', 'Fit'],
  ['pattern', 'Pattern'],
  ['neck', 'Neck'],
  ['sleeve', 'Sleeve'],
  ['occasion', 'Occasion'],
];

const FashionSpecsAccordion = ({ specs = null, description = '', product = null }) => {
  const [expanded, setExpanded] = useState(false);

  const detailRows = DETAIL_FIELDS.filter(([key]) => specs?.[key]);
  const care = Array.isArray(specs?.care) ? specs.care : [];
  const hasFit = Boolean(specs?.model_height && specs?.model_wearing_size);

  // SKU, HSN and barcode live on the product itself rather than in `specs`.
  const codeRows = [
    ['SKU', product?.sku],
    ['HSN Code', product?.hsn_code],
    ['Barcode', product?.barcode],
    ['Country of Origin', specs?.country_of_origin],
  ].filter(([, value]) => value);

  if (!description && detailRows.length === 0 && care.length === 0 && codeRows.length === 0) {
    return null;
  }

  const isLong = description && description.length > 220;
  const shown = isLong && !expanded ? `${description.slice(0, 220)}…` : description;

  return (
    <div className="mt-8">
      <div className="pt-6" style={{ borderTop: '1px solid var(--fx-line)' }}>
        <h3 className="fx-label mb-3">Product Details</h3>

        {description && (
          <p style={{ fontSize: 14, color: 'var(--fx-ink-2)', lineHeight: 1.6 }}>
            {shown}
            {isLong && (
              <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="ml-1"
                style={{ color: 'var(--fx-pink)', fontWeight: 700, fontSize: 14 }}
              >
                {expanded ? 'Less' : 'More'}
              </button>
            )}
          </p>
        )}

        {hasFit && (
          <p className="mt-3" style={{ fontSize: 14, color: 'var(--fx-ink-2)', lineHeight: 1.6 }}>
            The model ({specs.model_height}) is wearing a size {specs.model_wearing_size}
          </p>
        )}

        {detailRows.length > 0 && (
          <ul className="mt-4 space-y-1.5">
            {detailRows.map(([key, label]) => (
              <li key={key} style={{ fontSize: 14, color: 'var(--fx-ink-2)' }}>
                <span style={{ color: 'var(--fx-ink-3)' }}>{label}: </span>
                {specs[key]}
              </li>
            ))}
          </ul>
        )}
      </div>

      {care.length > 0 && (
        <div className="pt-6 mt-6" style={{ borderTop: '1px solid var(--fx-line)' }}>
          <h3 className="fx-label mb-3">Material &amp; Care</h3>
          {specs?.fabric && (
            <p style={{ fontSize: 14, color: 'var(--fx-ink-2)', marginBottom: 8 }}>{specs.fabric}</p>
          )}
          <ul className="space-y-1.5">
            {care.map((line) => (
              <li key={line} style={{ fontSize: 14, color: 'var(--fx-ink-2)' }}>{line}</li>
            ))}
          </ul>
        </div>
      )}

      {codeRows.length > 0 && (
        <div className="pt-6 mt-6" style={{ borderTop: '1px solid var(--fx-line)' }}>
          <h3 className="fx-label mb-3">Product Code &amp; Origin</h3>
          <ul className="space-y-1.5">
            {codeRows.map(([label, value]) => (
              <li key={label} style={{ fontSize: 14, color: 'var(--fx-ink-2)' }}>
                <span style={{ color: 'var(--fx-ink-3)' }}>{label}: </span>
                {value}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default FashionSpecsAccordion;
